// Browser polyfills
import '@babel/polyfill';
import 'whatwg-fetch';

// Libs
import React, { useEffect } from 'react';
import * as cloneDeep from 'lodash/cloneDeep';

// Routing
import { Route, routeHistory } from 'biw-web-router';
import { routes } from './scripts/app-routing.jsx';

// Apollo Client
import { ApolloProvider } from './providers';
import { Classes } from './styles/classes';

import { Layout } from 'antd';

// Components
import { AppHeader } from 'app/components/appHeader/AppHeader';
import { AppFooter } from 'app/components/appFooter/AppFooter';
import { PageContainer, PageContent } from 'app/components/common';
import { CookieAlert } from 'app/components/cookieAlert/CookieAlert';

// Styles
import './styles/app.less';

// Simple Auth
import { validateToken } from 'app/gql';

import { getEnv } from 'app/utils';
import { Events, EventType } from 'app/events';

/*******************************************************************************
 *  1. App (root level component)
 */

const App = () => {
    useEffect( () => {
        // This component is only rendered once when the user visits the page (login or not).
        // Because of this, we can use this callback as a way to begin any necessary app/state
        // initialization. We also know that if this component gets mounted, our services are
        // available and ready for use as needed.

        Events.invoke( EventType.STARTUP, { debug: true } );
    }, [] );

    return (
        <ApolloProvider>
            <Layout className={ Classes.APP }>
                <div className="app-content">
                    <AppHeader />
                    <PageContent>
                        <PageContainer>
                            { /* Render the page components chosen by the router. */ }
                            <Route render={ ( route ) => {
                                document.title = route && route.match ? route.match.title : 'Some default page title';
                                return route.match &&
                                    <>
                                        { React.cloneElement( route.component, { className: getRouteClassName( route.pathname, 'view' ) } ) }
                                    </>;
                            } } />
                        </PageContainer>
                    </PageContent>
                    <AppFooter />

                    { /* Show the cookie alert if acceptance is not in browser cache. */ }
                    <CookieAlert />
                </div>
            </Layout>
        </ApolloProvider>
    );
};

const getRouteClassName = ( pathname, prefix ) => {
    const classList = [ prefix ];

    // let prefix = '--';
    prefix = `${ prefix }-`;
    pathname.substring( 1 ).split( '/' ).forEach( ( part ) => {
        classList.push( `${ prefix }${ part }` );
        prefix = `${ prefix }${ part }-`;
    } );

    return classList.join( ' ' );
};

/*******************************************************************************
 *  2. Connect Routing
 */

// Middleware array
const middleWare = [
    async ( action, nextRoute, prevRoute ) => {
        const authToken = nextRoute.state.params.token;
        const requiresAuthentication = Boolean( nextRoute.match?.requireAuthentication );

        if ( requiresAuthentication ) {
            const validToken = await validateToken( authToken, getEnv( window.location.hostname ) );
            if ( validToken ) {
                action.ok();
            } else {
                action.redirect( '/' );
            }
        } else {
            action.ok();
        }

        // If the action hasn't been allowed or redirected yet, allow the
        // action. Without this, we might get stuck with the router and be
        // unable to view pages.
        action.ok();
    }
];

routeHistory.applyRoutes( routes );
routeHistory.applyMiddleware( middleWare );

// Each time the route history changes, store the new state.
routeHistory.addEventListener( 'changed', ( action, nextRoute, prevRoute ) => {
    routeHistory.route = cloneDeep( nextRoute );
} );


/*******************************************************************************
 * 3. Export the app for rendering
 */

export default App;
